import type { User } from "firebase/auth";
import type {
  DtoApplication,
  DtoApplicationBorrower,
} from "@porchpass/api-client";

export const getMainBorrowerIdFromApplication = (
  response: DtoApplication,
): string | undefined => {
  return response.borrowers?.find((borrower) => borrower.is_primary)?.id;
};

export const getCoBorrowerIdsFromApplication = (
  response: DtoApplication,
): string[] => {
  return (
    response.borrowers
      ?.filter((borrower) => !borrower.is_primary)
      .map((coborrower) => coborrower.id)
      .filter((id): id is string => Boolean(id)) ?? []
  );
};

export const getBorrowerFromApplicationById = (
  response: DtoApplication,
  borrowerId: string | undefined,
): DtoApplicationBorrower => {
  if (!borrowerId) return {};

  return (
    response.borrowers?.find((borrower) => borrower.id === borrowerId) || {}
  );
};

export const getAuthBorrowerInfo = (
  application: DtoApplication,
  firebaseUser: User | null,
): DtoApplicationBorrower => {
  return (
    application.borrowers?.find(
      (borrower) => borrower.user_id === firebaseUser?.uid,
    ) || {}
  );
};

export const getInProgressMainBorrowerApplication = (
  applications: DtoApplication[] | null,
  firebaseUser: User | null,
) => {
  return applications?.find(
    (application) =>
      application.status !== "SUBMITTED" &&
      application.status !== "REVIEWED" &&
      application.borrowers?.find(
        (borrower) =>
          borrower.user_id === firebaseUser?.uid && borrower.is_primary,
      ),
  );
};

export const getCompletedMainBorrowerApplication = (
  applications: DtoApplication[] | null,
  firebaseUser: User | null,
) => {
  return applications?.find(
    (application) =>
      (application.status === "SUBMITTED" ||
        application.status === "REVIEWED") &&
      application.borrowers?.find(
        (borrower) =>
          borrower.user_id === firebaseUser?.uid && borrower.is_primary,
      ),
  );
};

type SignUpFieldLockParams = {
  authEmail?: string | null;
  authPhone?: string | null;
  storedEmail: string | null;
  storedPhone: string | null;
  isRegisterUrl: boolean;
  /** Contact info already saved on the borrower; pass null while registering. */
  savedEmail?: string | null;
  savedPhone?: string | null;
};

/**
 * The email/phone a borrower signed in with identifies their Firebase user, so
 * it can't be edited here: changing it wouldn't update Firebase, and signing in
 * with the new value would start a new application. On /register the field is
 * locked once it's been prefilled from sign-in; everywhere else it's always
 * locked when Firebase has it.
 *
 * The API also rejects any DLA change to an email or phone the borrower has
 * already saved (only the Dealer Portal may change it), so a saved value is
 * locked too. A missing value stays editable, because adding one is allowed.
 */
export const getSignUpFieldLocks = ({
  authEmail,
  authPhone,
  storedEmail,
  storedPhone,
  isRegisterUrl,
  savedEmail,
  savedPhone,
}: SignUpFieldLockParams) => {
  const lockEmail =
    (!!authEmail && (!!storedEmail || !isRegisterUrl)) || !!savedEmail;
  const lockPhone =
    (!!authPhone && (!!storedPhone || !isRegisterUrl)) || !!savedPhone;

  return { email: lockEmail, confirmEmail: lockEmail, phone: lockPhone };
};
